import { useContext, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { t } from 'i18next'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined'

import { FormHeader, checkHasLink } from '../FormHeader'
import {
  CampaignContext,
  contentTypeEnum,
} from 'src/context/CampaignFormContext'
import { StepperContext } from 'src/context/StepperContext'
import { FormButtons } from '../../FormButtons'
import { ImageCampaignPreview } from '../../../Preview/ImageCampaignPreview'
import { PreviewModal, PreviewMode } from 'src/components/Modal/PreviewModal'
import { MediaIntervalSelect } from '../MediaIntervalSelect'
import { goToCampaigns } from 'src/routes/coordinator'
import { useParams } from 'react-router-dom'
import { FileRepositorySelectorModal } from 'src/pages/FileRepository/components/FileRepositorySelectorModal'

interface Props {
  isViewMode?: boolean
  disabled?: any
}
export const ImageForm = ({ isViewMode, disabled }: Props) => {
  const [showPreview, setShowPreview] = useState(false)
  const [previewMode, setPreviewMode] = useState(PreviewMode.view)
  const [showFileSelector, setShowFileSelector] = useState(false)
  const {
    setContentType,
    formState: { setValue, watch, reset },
  } = useContext(CampaignContext)
  const { nextStep } = useContext(StepperContext)
  const { id } = useParams()
  const isEdit = Boolean(id)

  const mediaURL = watch('content.mediaURL')
  const imageName = watch('content.imageName')

  const formIsValid =
    Boolean(mediaURL) &&
    Boolean(!checkHasLink(watch('content.description')))

  const clearForm = () => {
    setValue('content.title', '')
    setValue('content.description', '')
    setValue('content.mediaURL', null)
    setValue('content.imageName', '')
    setValue('content.mediaInterval', 0)
    setValue('content.hasMediaInterval', false)
    setValue('content.titleColor', undefined)
    setValue('content.descriptionColor', undefined)
  }

  const handleFileSelect = (fileUrl: string, fileName: string) => {
    setValue('content.mediaURL', fileUrl)
    setValue('content.imageName', fileName)
  }

  const handleRemoveFile = () => {
    setValue('content.mediaURL', null)
    setValue('content.imageName', '')
  }

  return (
    <Box>
      <PreviewModal
        handleClose={() => setShowPreview(false)}
        open={showPreview}
        title={`${t('campaign.preview.visualization.off')}\n${t(
          'campaign.image.title'
        )}`}
        mode={previewMode}
        onConfirm={() => {
          setShowPreview(false)
          nextStep()
        }}
      >
        <ImageCampaignPreview />
      </PreviewModal>

      <FileRepositorySelectorModal
        open={showFileSelector}
        onClose={() => setShowFileSelector(false)}
        onSelect={handleFileSelect}
        mimeFilter="image"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <FormHeader title={'campaign.image.title'} isViewMode={isViewMode} />

        {/* File selector area */}
        <Box sx={{ my: 3 }}>
          {mediaURL ? (
            /* ── Selected file display with thumbnail ──────── */
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '90px',
                border: '1px solid #FF5E1E',
                borderBottom: 'solid 4px #FF5E1E',
                borderRadius: '4px 4px 0px 0px',
                backgroundColor: 'rgba(255, 94, 30, 0.08)',
                gap: '12px',
                cursor: isViewMode ? 'pointer' : 'default',
                overflow: 'hidden',
              }}
              onClick={() => {
                if (isViewMode && mediaURL) {
                  const link = document.createElement('a')
                  link.download = mediaURL
                  link.href = mediaURL
                  link.click()
                }
              }}
            >
              {/* Image thumbnail */}
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              >
                <img
                  src={mediaURL}
                  alt={imageName || ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: '#FF5E1E',
                  fontWeight: 500,
                  fontSize: '14px',
                  maxWidth: '250px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {imageName || t('fileRepository.selector.fileSelected', 'Arquivo selecionado')}
              </Typography>
              {!isViewMode && (
                <DeleteForeverIcon
                  sx={{
                    color: '#00000099',
                    cursor: 'pointer',
                    zIndex: 4,
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile()
                  }}
                />
              )}
            </Box>
          ) : (
            /* ── Select file button ────────────────────────── */
            <Box
              onClick={() => !isViewMode && setShowFileSelector(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '100%',
                height: '90px',
                cursor: isViewMode ? 'default' : 'pointer',
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='rgba(0, 0, 0, 0.6)' stroke-width='4' stroke-dasharray='15%2c 20' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                gap: '8px',
                color: 'rgba(0, 0, 0, 0.6)',
                ':hover': {
                  color: '#FF5E1E',
                  backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='rgb(255, 94, 30)' stroke-width='4' stroke-dasharray='15%2c 20' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <FolderOpenOutlined sx={{ width: '25px', height: '25px' }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '16px',
                  }}
                >
                  {t('fileRepository.selector.selectFromRepository', 'Selecionar do Repositório de Arquivos')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('input.upload.file.acceptedformats')}
              </Typography>
            </Box>
          )}
        </Box>

        <MediaIntervalSelect disabled={disabled} />

        <Box sx={{ width: '100%', p: 'none' }}>
          {!isViewMode && (
            <FormButtons
              onClean={() => clearForm()}
              nextButtonEnabled={formIsValid}
              prevButtonEnabled={true}
              formIsValid={formIsValid}
              cleanButtonEnabled={formIsValid}
              previewButtonEnabled={formIsValid}
              prevButtonAction={() => {
                reset({})
                setContentType(contentTypeEnum.NONE)
              }}
              previewButtonAction={() => {
                setPreviewMode(PreviewMode.view)
                setShowPreview(true)
              }}
              nextButtonAction={() => {
                setPreviewMode(PreviewMode.confirm)
                setShowPreview(true)
              }}
              nextButtonCta="form.button.next"
              nextButtonArrow
              isEdit={isEdit}
              goTo={goToCampaigns}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
